import { useEffect, useState, useCallback, useRef } from "react";
import { useAtom } from "jotai";
import { AnimatePresence, motion } from "framer-motion";

import { Video } from "@/components/atoms/Video";
import { VideoControls } from "@/components/molecules/VideoControls";
import { VideoControlsBottom } from "@/components/molecules/VideoControlsBottom";
import { VideoControlsTop } from "@/components/molecules/VideoControlsTop";
import { SubtitleOverlay } from "@/components/molecules/SubtitleOverlay";
import { Spinner } from "@/components/atoms/Spinner";
import { useClassNames } from "@/utils/classNames";
import { useVideoPlayer } from "@/utils/hooks/useVideoPlayer";
import { isModalOpenAtom, videoPlayerAtom } from "@/constants/atoms";
import { checkIfMobileDevice } from "@/utils/hooks/useDeviceDetection";

import {
  VideoPlayerProps,
  ScreenOrientationWithLock,
  OrientationLockType,
} from "./types";
import styles from "./VideoPlayer.module.scss";

const SPINNER_DURATION = 2000; // ms
const HIDE_CONTROLS_DURATION = 6000; // ms
const DISMISS_CONTROLS_DELAY = 200; // ms
/**
 * Video Player organism
 * Can be displayed inline or as a fullscreen modal with landscape orientation lock
 *
 * @param props - The video player properties
 * @returns the video player component
 */
const VideoPlayer = ({
  videoProps,
  className,
  skipSeconds = 10,
  showControls = true,
  isModal = false,
  onClose,
  loopStart,
  loopEnd,
}: VideoPlayerProps) => {
  const {
    videoRef,
    isPlaying,
    currentTime,
    duration,
    handlePlay,
    handlePause,
    handleEnded,
    handlePlayPause,
    handleSkipBackward,
    handleSkipForward,
    handleSeek,
  } = useVideoPlayer();
  const isMobileDevice = checkIfMobileDevice();
  const [isOpen, setIsOpen] = useAtom(isModalOpenAtom);
  const [videoPlayerState, setVideoPlayerState] = useAtom(videoPlayerAtom);
  const [showControlsVisible, setShowControlsVisible] = useState(false);
  const [showSpinner, setShowSpinner] = useState(true);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { isScrubbing, isAdjustingBrightness, isSubtitleOverlayOpen } =
    videoPlayerState;
  const modalRef = useRef<HTMLDivElement>(null);
  const wasPlayingBeforeOverlayRef = useRef<boolean>(false);
  const controlsVisibleBeforeOverlayRef = useRef<boolean>(false);

  /** Reset the auto-hide timer for controls */
  const resetControlsTimer = useCallback(() => {
    setShowControlsVisible(true);

    /** Clear existing timer */
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
    }

    /** Start new timer */
    hideTimerRef.current = setTimeout(() => {
      setShowControlsVisible(false);
    }, HIDE_CONTROLS_DURATION);
  }, []);

  /** Handle tap on controls container */
  const handleControlsContainerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (showControlsVisible) {
        /** Controls are visible, dismiss with delay to allow double-taps */
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = setTimeout(() => {
          setShowControlsVisible(false);
          hideTimerRef.current = null;
        }, DISMISS_CONTROLS_DELAY);
      } else {
        /** Controls are hidden, show them and start timer */
        resetControlsTimer();
      }
    },
    [showControlsVisible, resetControlsTimer],
  );

  /** Handle interaction with actual control buttons - reset timer */
  const handleControlsInteraction = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;

      /** Allow skip button tap areas to propagate (for single-click dismissal) */
      if (target.dataset.tapArea === "true") {
        return;
      }

      /** Only prevent propagation if clicking on actual controls, not the overlay */
      if (target.closest('[role="button"], button')) {
        e.stopPropagation();
        resetControlsTimer();
      }
    },
    [resetControlsTimer],
  );

  /** Loop segment for inline autoplay for clips */
  const handleTimeUpdate = useCallback(() => {
    const player = videoRef.current;
    if (!player) return;
    if (isModal) return;
    if (typeof loopStart !== "number" || typeof loopEnd !== "number") return;
    if (loopEnd <= loopStart) return;

    if (player.currentTime >= loopEnd) {
      /* Jump back to segment start to loop */
      player.currentTime = loopStart;
      /* Ensure playback continues */
      player.play().catch(() => {});
    }
  }, [isModal, loopStart, loopEnd, videoRef]);

  const orientation = screen.orientation as unknown as
    | ScreenOrientationWithLock
    | undefined;

  /** Store videoRef in atom on mount */
  useEffect(() => {
    setVideoPlayerState((prev) => ({ ...prev, videoRef, resetControlsTimer }));
  }, [videoRef, setVideoPlayerState, resetControlsTimer]);

  /** Cleanup timer on unmount */
  useEffect(
    () => () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    },
    [],
  );

  /** Spinner and autoplay effect - handles spinner display and video playback timing */
  useEffect(() => {
    if (isModal && isOpen) {
      setShowSpinner(true);

      const player = videoRef.current;

      /** Hide spinner after fixed duration */
      const spinnerTimer = setTimeout(() => {
        setShowSpinner(false);
      }, SPINNER_DURATION);

      /** Attempt to play video after spinner duration */
      if (player) {
        const attemptPlay = () => {
          player.play().catch(() => {
            /** Autoplay was blocked, ignore */
          });
        };

        /** Wait for spinner duration before starting playback */
        const playTimer = setTimeout(attemptPlay, SPINNER_DURATION);
        const retryTimer = setTimeout(attemptPlay, SPINNER_DURATION + 300);

        return () => {
          clearTimeout(spinnerTimer);
          clearTimeout(playTimer);
          clearTimeout(retryTimer);
        };
      }

      return () => clearTimeout(spinnerTimer);
    }
  }, [isModal, isOpen]);

  /** Hide controls when subtitle overlay opens, restore when it closes */
  useEffect(() => {
    if (isSubtitleOverlayOpen) {
      // Store current controls visibility state
      controlsVisibleBeforeOverlayRef.current = showControlsVisible;

      // Hide controls and clear timer
      setShowControlsVisible(false);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    } else if (controlsVisibleBeforeOverlayRef.current) {
      // Restore controls if they were visible before
      resetControlsTimer();
    }
  }, [isSubtitleOverlayOpen, showControlsVisible, resetControlsTimer]);

  /** Prevent body scroll when modal is open */
  useEffect(() => {
    document.body.style.overflow = isModal && isOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isModal, isOpen]);

  /** Pause video when subtitle overlay opens, resume when it closes */
  useEffect(() => {
    const player = videoRef.current;
    if (!player) return;

    if (isSubtitleOverlayOpen) {
      /** Store whether video was playing before opening overlay */
      wasPlayingBeforeOverlayRef.current = !player.paused;
      /** Pause the video */
      player.pause();
    } else if (wasPlayingBeforeOverlayRef.current) {
      /** Resume playback if it was playing before */
      player.play().catch(() => {
        /** Playback was blocked, ignore */
      });
      wasPlayingBeforeOverlayRef.current = false;
    }
  }, [isSubtitleOverlayOpen, videoRef]);

  /** Modal-specific effects - fullscreen and orientation */
  useEffect(() => {
    if (isModal && isOpen) {
      /** Request fullscreen and lock orientation for Android/mobile compatibility */
      const setupFullscreenAndOrientation = async () => {
        try {
          /** Step 1: Request fullscreen only on mobile devices (required for orientation lock on Android) */
          if (
            isMobileDevice &&
            modalRef.current &&
            document.fullscreenEnabled
          ) {
            await modalRef.current.requestFullscreen();
          }

          /** Step 2: Lock screen orientation to landscape */
          if (orientation?.lock) {
            await orientation.lock(OrientationLockType.Landscape);
          }
        } catch (err) {
          console.warn("Failed to setup fullscreen/orientation:", err);
        }
      };

      setupFullscreenAndOrientation();
    } else if (isModal && !isOpen) {
      /** Exit fullscreen and restore orientation when modal closes */
      const cleanup = async () => {
        /** Exit fullscreen if active */
        if (document.fullscreenElement) {
          try {
            await document.exitFullscreen();
          } catch (err) {
            console.warn("Failed to exit fullscreen:", err);
          }
        }

        /** Restore orientation to portrait-primary */
        if (orientation?.lock) {
          try {
            await orientation.lock(OrientationLockType.PortraitPrimary);
          } catch (err) {
            console.warn("Failed to restore orientation to portrait:", err);
          }
        }
      };

      cleanup();
    }
  }, [isModal, isOpen, orientation]);

  const modalOverlayCn = useClassNames(styles, styles["modal-overlay"]);

  const videoPlayerCn = useClassNames(
    styles,
    styles["video-player"],
    className,
  );

  const controlsContainerCn = useClassNames(
    styles,
    styles["controls-container"],
    showControlsVisible && styles["show"],
  );

  const topControlsCn = useClassNames(
    styles,
    styles["top-video-controls"],
    showControlsVisible &&
      !isScrubbing &&
      !isAdjustingBrightness &&
      styles["show"],
  );

  const bottomControlsCn = useClassNames(
    styles,
    styles["bottom-video-controls"],
    (showControlsVisible || isScrubbing || isAdjustingBrightness) &&
      styles["show"],
  );

  const centerControlsCn = useClassNames(
    styles,
    styles["video-controls"],
    showControlsVisible &&
      !isScrubbing &&
      !isAdjustingBrightness &&
      styles["show"],
  );

  const controlsOverlayCn = useClassNames(
    styles,
    styles["controls-overlay"],
    isAdjustingBrightness && styles["hidden"],
  );

  const videoPlayerContent = (
    <div
      className={styles["video-container"]}
      onClick={handleControlsContainerClick}
    >
      {isModal && showSpinner && (
        <div className={styles["spinner-overlay"]}>
          <Spinner />
        </div>
      )}
      <Video
        {...videoProps}
        ref={videoRef}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
      />
      {isModal && showControls && (
        <div
          className={controlsContainerCn}
          onClick={handleControlsInteraction}
        >
          <VideoControlsTop
            onClose={() => setIsOpen(false)}
            className={topControlsCn}
          />
          <VideoControls
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onSkipBackward={() => handleSkipBackward(skipSeconds)}
            onSkipForward={() => handleSkipForward(skipSeconds)}
            skipSeconds={skipSeconds}
            className={centerControlsCn}
          />
          <VideoControlsBottom
            videoRef={videoRef}
            onSeek={handleSeek}
            className={bottomControlsCn}
          />
          <div className={controlsOverlayCn} />
        </div>
      )}
      {isModal && <SubtitleOverlay />}
    </div>
  );

  /** Render as modal */
  if (isModal) {
    return (
      <AnimatePresence
        onExitComplete={() => {
          if (onClose) {
            onClose();
          }
        }}
      >
        {isOpen && (
          <motion.div
            ref={modalRef}
            className={modalOverlayCn}
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className={styles["video-player-container"]}
              onClick={(e) => e.stopPropagation()}
            >
              {videoPlayerContent}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  /** Render inline */
  return <div className={videoPlayerCn}>{videoPlayerContent}</div>;
};

export { VideoPlayer };
